import { api } from '@/util/api'
import type { AthleteFields, AthleteRecord, Camera, Race, RowResult, TrackerRecord, TrackingProvider, TrackingSource, WebhookDelivery } from './types'

/** The API takes up to this many rows per batch (Roster::BATCH_LIMIT) and returns this many per page. */
export const BATCH_SIZE = 1000

/** Every athlete on the roster, a page at a time. */
export async function loadAthletes(eventId: string): Promise<AthleteRecord[]> {
  const athletes: AthleteRecord[] = []
  for (;;) {
    const page = await api.get<{ data: AthleteRecord[]; meta: { total: number } }>(`/events/${eventId}/athletes`, {
      limit: BATCH_SIZE,
      offset: athletes.length,
    })
    athletes.push(...page.data)
    if (page.data.length === 0 || athletes.length >= page.meta.total) return athletes
  }
}

/** Every tracker of the event, assigned or not, a page at a time. */
export async function loadTrackers(eventId: string): Promise<TrackerRecord[]> {
  const trackers: TrackerRecord[] = []
  for (;;) {
    const page = await api.get<{ data: TrackerRecord[] }>(`/events/${eventId}/trackers`, { limit: BATCH_SIZE, offset: trackers.length })
    trackers.push(...page.data)
    if (page.data.length < BATCH_SIZE) return trackers
  }
}

export async function loadRaces(eventId: string): Promise<Race[]> {
  const res = await api.get<{ data: Race[] }>(`/events/${eventId}/routes`)
  return res.data
}

/** The event's cameras (programmes left out), for giving one a tracker. */
export async function loadCameras(eventId: string): Promise<Camera[]> {
  const res = await api.get<{ data: { id: string; attributes: { name: string; source_role?: string } }[] }>('/streams', { event_id: eventId })
  return res.data.filter((stream) => stream.attributes.source_role !== 'programme').map((stream) => ({ id: stream.id, name: stream.attributes.name }))
}

export async function loadTrackingSources(eventId: string): Promise<TrackingSource[]> {
  const res = await api.get<{ data: TrackingSource[] }>(`/events/${eventId}/destination/data-sources`)
  return res.data.filter((source) => source.capabilities.includes('tracking.v1'))
}

export async function loadLastDelivery(eventId: string, sourceId: string): Promise<WebhookDelivery | null> {
  const res = await api.get<{ data: WebhookDelivery[] }>(`/events/${eventId}/destination/data-sources/${sourceId}/webhook-deliveries`, { limit: 1 })
  return res.data[0] ?? null
}

/**
 * The tracking providers the server knows (docs/features/athletes-and-trackers.md): its adapters that feed trackers.
 * LNX is the first; a new one appears here once the server has it.
 */
export async function loadProviders(eventId: string): Promise<TrackingProvider[]> {
  const res = await api.get<{ data: (TrackingProvider & { capabilities: string[] })[] }>(`/events/${eventId}/destination/adapters`)
  return res.data
    .filter((adapter) => adapter.capabilities.includes('tracking.v1'))
    .map(({ key, name, version, transport }) => ({ key, name, version, transport: transport === 'push' ? 'push' : 'poll' }))
}

/** A source key for another source of `provider`: its key, then key-2, key-3… past the ones the event has. */
export function nextSourceKey(provider: TrackingProvider, sources: readonly TrackingSource[]): string {
  const taken = new Set(sources.map((source) => source.source_key))
  if (!taken.has(provider.key)) return provider.key
  let n = 2
  while (taken.has(`${provider.key}-${n}`)) n++
  return `${provider.key}-${n}`
}

/** Connects a provider to the event; a retry of the same connection answers the same source. */
export async function connectProvider(eventId: string, provider: TrackingProvider, sourceKey: string): Promise<TrackingSource> {
  const res = await api.post<{ data: TrackingSource }>(`/events/${eventId}/destination/data-sources`, {
    idempotency_key: `connect-${sourceKey}-${eventId}`,
    data_source: { source_key: sourceKey, adapter_key: provider.key, adapter_version: provider.version, capabilities: ['tracking.v1'] },
  })
  return res.data
}

/** Disconnects a source: its webhook stops working and its trackers go. */
export async function disconnectSource(eventId: string, sourceId: string): Promise<void> {
  await api.delete<void>(`/events/${eventId}/destination/data-sources/${sourceId}`)
}

export async function rotateWebhook(eventId: string, sourceId: string): Promise<TrackingSource> {
  const res = await api.post<{ data: TrackingSource }>(`/events/${eventId}/destination/data-sources/${sourceId}/webhook-token`)
  return res.data
}

export async function createAthlete(eventId: string, fields: AthleteFields): Promise<AthleteRecord> {
  return (await api.post<{ data: AthleteRecord }>(`/events/${eventId}/athletes`, fields)).data
}

export async function updateAthlete(eventId: string, id: string, fields: AthleteFields): Promise<AthleteRecord> {
  return (await api.patch<{ data: AthleteRecord }>(`/events/${eventId}/athletes/${id}`, fields)).data
}

export async function removeAthlete(eventId: string, id: string): Promise<void> {
  await api.delete<void>(`/events/${eventId}/athletes/${id}`)
}

export async function uploadPhoto(eventId: string, id: string, file: File): Promise<AthleteRecord> {
  const body = new FormData()
  body.append('photo', file)
  return (await api.putMultipart<{ data: AthleteRecord }>(`/events/${eventId}/athletes/${id}/photo`, body)).data
}

/** Adds or updates athletes BATCH_SIZE rows at a time; each row's result keeps its place in `rows`. */
export async function importAthletes(eventId: string, rows: AthleteFields[], onProgress?: (done: number) => void): Promise<RowResult[]> {
  const results: RowResult[] = []
  for (let start = 0; start < rows.length; start += BATCH_SIZE) {
    const res = await api.post<{ data: RowResult[] }>(`/events/${eventId}/athletes/batch`, { athletes: rows.slice(start, start + BATCH_SIZE) })
    results.push(...res.data.map((result) => ({ ...result, index: result.index + start })))
    onProgress?.(Math.min(start + BATCH_SIZE, rows.length))
  }
  return results
}

/** What a tracker stands for: an athlete by bib or id, a camera, or nothing. */
export async function assignTracker(
  eventId: string,
  trackerId: string,
  subject: { bib: string } | { athlete_id: string } | { stream_id: string } | Record<string, never>,
): Promise<TrackerRecord> {
  return (await api.patch<{ data: TrackerRecord }>(`/events/${eventId}/trackers/${trackerId}`, subject)).data
}

/** The API's own words for a failed write, or `fallback`. */
export function apiErrors(failure: unknown, fallback: string): string {
  const body = (failure as { response?: { data?: { error?: { errors?: unknown; message?: string } } } })?.response?.data?.error
  const errors = body?.errors
  if (Array.isArray(errors) && errors.length) return errors.join('. ')
  if (errors && typeof errors === 'object') return Object.values(errors as Record<string, string[]>).flat().join('. ') || fallback
  return body?.message || fallback
}
